/**
 * NfseActiveSection.js
 * Exibe os detalhes e ações de uma NFS-e ativa (queued / processing / issued).
 * O cancelamento usa ConfirmDialog em dois passos:
 *   1. Confirmação inicial ("Tem certeza?")
 *   2. Justificativa obrigatória (mín. 15 chars) antes de confirmar
 *
 * Props:
 *   activeInvoice — objeto da nota fiscal ativa
 *   appointment   — objeto do agendamento
 *   patientData   — objeto do paciente
 *   onRefresh     — callback para recarregar a lista de notas
 */

import React, { useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { buildFiscalFilename, GetFiscalPublicPdfUrl, describeFiscalAutoSend } from '../../../../../api/FiscalAPI';
import { getAccessToken } from '../../../../../api/authTokens';
import { SendWhatsAppMessage } from '../../../../../api/WhatsappAPI';
import { useWhatsAppSender } from '../../../../../contexts/WhatsAppSenderContext';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import { NFSE_STATUS, fmtDateTime } from './paymentHelpers';
import { procedureLabels } from '../../utils/appointmentUtils';
import { DlIcon, WaIcon, RefreshIcon } from './PaymentSectionIcons';
import styles from './AppointmentDetailPanel.module.css';

export default function NfseActiveSection({ activeInvoice, appointment, patientData, onRefresh }) {
  const {
    CancelFiscalInvoice,
    GetFiscalDownloadUrl,
    RefreshFiscalInvoiceStatus,
    GetResourceById,
    CompanyConfig,
    SendFiscalInvoiceWhatsApp,
  } = DataProviderInstance();

  // Com número próprio conectado a nota sai pelo sistema (link em botão), na hora.
  // Sem número próprio continua abrindo o WhatsApp Web: a nota nunca sai pelo
  // número da Gabriela.
  const { state: waSenderState } = useWhatsAppSender();
  const ownNumberConnected = waSenderState === 'own_connected';

  // ── Estado do dialog de cancelamento ─────────────────────────────────────────
  const [cancelStep,  setCancelStep]  = useState(null); // null | 'confirm' | 'justif'
  const [justif,      setJustif]      = useState('');
  const [canceling,   setCanceling]   = useState(false);
  const [cancelErr,   setCancelErr]   = useState('');

  // ── Estado de outras ações ────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);
  const [dlErr,      setDlErr]      = useState('');
  const [sendingWa,  setSendingWa]  = useState(false);
  const [waErr,      setWaErr]      = useState('');

  const cfg   = NFSE_STATUS[activeInvoice.status] || NFSE_STATUS.pending;
  const pdfOk = activeInvoice?.has_pdf === true;
  const xmlOk = activeInvoice?.has_xml === true;

  // ── Download PDF / XML ────────────────────────────────────────────────────────
  const download = (type) => {
    setDlErr('');
    const url      = GetFiscalDownloadUrl(activeInvoice.id, type);
    const token    = getAccessToken();
    const filename = buildFiscalFilename(activeInvoice, type);
    fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Arquivo não disponível.'); return r.blob(); })
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); window.URL.revokeObjectURL(u);
      })
      .catch(e => setDlErr(e.message));
  };

  // ── Enviar por WhatsApp ───────────────────────────────────────────────────────
  const sendWa = () => {
    const phone = patientData?.phone;
    if (!phone) return;

    if (ownNumberConnected) {
      setWaErr('');
      setSendingWa(true);
      SendFiscalInvoiceWhatsApp(activeInvoice.id, (r) => {
        setSendingWa(false);
        if (r?.success) { onRefresh?.(); return; } // a linha de situação do envio vem do refresh
        setWaErr(r?.error || 'Não foi possível enviar a nota por WhatsApp.');
      });
      return;
    }

    const pdfUrl = activeInvoice?.public_key ? GetFiscalPublicPdfUrl(activeInvoice.public_key) : null;
    const res    = GetResourceById(appointment?.resource)?.Label || '';
    const proc   = procedureLabels(appointment, CompanyConfig?.Procedures);
    const dt     = (appointment?.date || '').split(' ')[0].split('-').reverse().join('/');
    const parts  = [res && `com ${res}`, proc, dt && `no dia ${dt}`].filter(Boolean);
    let msg = `Olá! Segue sua nota fiscal referente ao atendimento realizado${parts.length ? ' ' + parts.join(', ') : ''}.`;
    if (pdfUrl) msg += `\n\n${pdfUrl}`;
    // DDI do paciente — sem ele, DDD 33 (por exemplo) é lido como +33 (França)
    SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(msg), patientData?.countryCode);
  };

  // ── Atualizar status ──────────────────────────────────────────────────────────
  const refresh = () => {
    setRefreshing(true);
    RefreshFiscalInvoiceStatus(activeInvoice.id, (r) => {
      setRefreshing(false);
      if (r?.success) onRefresh?.();
    });
  };

  // ── Cancelamento — passo 1: abre confirmação ──────────────────────────────────
  const openCancelConfirm = () => {
    setJustif('');
    setCancelErr('');
    setCancelStep('confirm');
  };

  // ── Cancelamento — passo 2: abre justificativa ────────────────────────────────
  const proceedToJustif = () => {
    setCancelStep('justif');
  };

  // ── Cancelamento — executa ────────────────────────────────────────────────────
  const executarCancelamento = () => {
    if (justif.trim().length < 15) { setCancelErr('Mínimo 15 caracteres.'); return; }
    setCanceling(true); setCancelErr('');
    CancelFiscalInvoice({ invoiceId: activeInvoice.id, justificativa: justif.trim() }, (r) => {
      setCanceling(false);
      if (r?.success) { setCancelStep(null); onRefresh?.(); }
      else {
        const e = r?.error;
        setCancelErr(typeof e === 'object' ? e.message : (e || 'Erro ao cancelar.'));
      }
    });
  };

  const closeCancelDialog = () => {
    if (canceling) return;
    setCancelStep(null);
    setCancelErr('');
    setJustif('');
  };

  return (
    <div className={styles.nfseSection}>

      {/* ── Cabeçalho: status + botão cancelar ── */}
      <div className={styles.nfseSectionHeader}>
        <div className={styles.nfseStatusRow}>
          <span className={styles.nfseStatusDot} style={{ background: cfg.color }} />
          <span className={styles.nfseStatusLabel}>{cfg.label}</span>
          {activeInvoice.invoice_number && (
            <span className={styles.nfseNumber}>Nº {activeInvoice.invoice_number}</span>
          )}
        </div>
        {activeInvoice.status === 'issued' && (
          <button type="button" className={styles.nfseCancelTrigger}
            onClick={openCancelConfirm}>
            Cancelar NFS-e
          </button>
        )}
      </div>

      {/* ── Detalhes ── */}
      {activeInvoice.issued_at && (
        <div className={styles.nfseDetailRow}>
          <span className={styles.nfseDetailLabel}>Emitida em</span>
          <span className={styles.nfseDetailValue}>{fmtDateTime(activeInvoice.issued_at)}</span>
        </div>
      )}
      {activeInvoice.amount && (
        <div className={styles.nfseDetailRow}>
          <span className={styles.nfseDetailLabel}>Valor</span>
          <span className={styles.nfseDetailValue}>
            R$ {parseFloat(activeInvoice.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
        </div>
      )}
      {activeInvoice.service_description && (
        <div className={styles.nfseDetailRow}>
          <span className={styles.nfseDetailLabel}>Descrição</span>
          <span className={styles.nfseDetailValue}>{activeInvoice.service_description}</span>
        </div>
      )}
      {activeInvoice.error_message && (
        <div className={styles.nfseErrorMsg}>{activeInvoice.error_message}</div>
      )}
      {dlErr && <div className={styles.nfseErrorMsg}>{dlErr}</div>}
      {waErr && <div className={styles.nfseErrorMsg}>{waErr}</div>}

      {/* ── Ações ── */}
      <div className={styles.nfseActions}>
        {activeInvoice.status === 'processing' && (
          <button type="button" className={styles.nfseActionBtn}
            onClick={refresh} disabled={refreshing}>
            <RefreshIcon />
            <span>{refreshing ? 'Atualizando...' : 'Atualizar status'}</span>
          </button>
        )}
        {activeInvoice.status === 'issued' && (
          <>
            <button type="button" className={styles.nfseActionBtn}
              onClick={() => download('pdf')} disabled={!pdfOk}
              title={!pdfOk ? 'PDF ainda sendo processado...' : 'Baixar PDF'}>
              <DlIcon /><span>PDF</span>
            </button>
            <button type="button" className={styles.nfseActionBtn}
              onClick={() => download('xml')} disabled={!xmlOk}
              title={!xmlOk ? 'XML ainda sendo processado...' : 'Baixar XML'}>
              <DlIcon /><span>XML</span>
            </button>
            <button
              type="button"
              className={`${styles.nfseActionBtn} ${styles.nfseActionBtnWa}`}
              onClick={sendWa}
              disabled={!patientData?.phone || !pdfOk || sendingWa}
              title={!patientData?.phone ? 'Paciente sem telefone' : !pdfOk ? 'PDF ainda sendo processado...' : 'Enviar por WhatsApp'}
            >
              <WaIcon /><span>{sendingWa ? 'Enviando...' : 'Enviar'}</span>
            </button>
          </>
        )}
      </div>

      {activeInvoice.status === 'issued' && !pdfOk && (
        <div className={styles.nfsePdfWaiting}>
          <span className={styles.spinner} />
          <span>Aguardando geração do PDF...</span>
        </div>
      )}
      {activeInvoice.status === 'issued' && activeInvoice.auto_send && (
        <div className={styles.nfseAutoSend}>
          <WaIcon />
          <span>{describeFiscalAutoSend(activeInvoice.auto_send)}</span>
        </div>
      )}

      {/* ── Dialog passo 1: confirmação inicial ── */}
      <ConfirmDialog
        isOpen={cancelStep === 'confirm'}
        title="Cancelar nota fiscal?"
        message="Esta ação é irreversível. A NFS-e será cancelada junto à prefeitura. Deseja continuar?"
        confirmLabel="Sim, continuar"
        cancelLabel="Não, voltar"
        variant="danger"
        onConfirm={proceedToJustif}
        onCancel={closeCancelDialog}
      />

      {/* ── Dialog passo 2: justificativa ── */}
      <ConfirmDialog
        isOpen={cancelStep === 'justif'}
        title="Informe o motivo do cancelamento"
        message="O motivo é obrigatório e deve ter no mínimo 15 caracteres."
        confirmLabel="Confirmar cancelamento"
        cancelLabel="Voltar"
        variant="danger"
        onConfirm={executarCancelamento}
        onCancel={closeCancelDialog}
        confirmDisabled={justif.trim().length < 15}
        loading={canceling}
      >
        <textarea
          className={styles.invoiceFormTextarea}
          value={justif}
          onChange={e => { setJustif(e.target.value); setCancelErr(''); }}
          rows={3}
          maxLength={255}
          placeholder="Ex: Nota emitida com dados incorretos."
          disabled={canceling}
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          {cancelErr
            ? <span className={styles.invoiceFormError}>{cancelErr}</span>
            : <span />}
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{justif.length}/255</span>
        </div>
      </ConfirmDialog>

    </div>
  );
}
